import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import styled from "styled-components";
import HtmlWithVocab from "./HtmlWithVocab";
import TranscriptPlayer from "./TranscriptPlayer";
import { highlightTextIn, clearHighlightsIn } from "../../utils/textHighlighter";

/**
 * 詳解頁（正式模考）。
 *
 * 從 dashboard 的已完成卡片進來（三科都交卷後才會出現按鈕），
 * 資料來自 POST /exam/details/{id}/review——密碼就是學生已經輸過、
 * App 記在記憶體裡的那組總密碼，不用再輸一次。
 *
 * 三科的版面：
 *   閱讀  左邊逐題（你的作答／正解／老師詳解）、右邊文章（單字可點、
 *         釘住自己捲）。老師標過重點句的題目，點題目文章就捲到那句
 *         並套色——Edric 指定的對照方式。
 *   聽力  逐題列表＋逐字稿（點句子重播該句，句裡的單字也可點）
 *   寫作  自己的作文對照老師範文與劍橋範文
 * 頁首如果教務有掛詳解影片，放在最上面。
 */

// 鎖定的詳解（Edric 2026-10-20 定的文案）：考試站的學生沒有學習系統帳號、無從開通
export const LOCKED_TEXT = "🔒 詳解試閱，完整版請洽教務";

const StyledReview = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  background: #f4f5f7;
  color: #1d2430;
  font-family: Arial, "Noto Sans TC", sans-serif;
  text-align: left; /* 全站 .App 置中，這頁要靠左 */

  .ce-rv-inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 24px 24px 10vh;
  }

  .ce-rv-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 18px;

    h1 {
      font-size: 22px;
      margin: 0;
    }
    .ce-rv-score {
      font-size: 15px;
      color: #2f5d50;
      font-weight: 700;
    }
  }

  .ce-rv-back {
    border: 1px solid #cfd6dd;
    background: #fff;
    border-radius: 6px;
    padding: 8px 18px;
    font-size: 14px;
    cursor: pointer;
  }

  .ce-rv-block {
    background: #fff;
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

    h2 {
      font-size: 17px;
      margin: 0 0 12px;
      color: #2f5d50;
    }
    h3 {
      font-size: 15px;
      margin: 14px 0 8px;
    }
  }

  .ce-rv-columns {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .ce-rv-col {
    flex: 1 1 460px;
    min-width: 320px;
  }
  /* 閱讀的文章欄釘住、自己捲自己的——捲到第 30 題時文章還在旁邊，
     對照才不會斷。窄螢幕兩欄會疊起來，釘住反而擋路，所以只在寬版生效。 */
  @media (min-width: 1000px) {
    .ce-rv-col--pin {
      position: sticky;
      top: 16px;
      max-height: calc(100vh - 32px);
      overflow-y: auto;
    }
  }

  .ce-rv-passage {
    font-size: 15px;
    line-height: 1.85;
    img {
      max-width: 100%;
    }
  }

  .ce-rv-video {
    margin-bottom: 12px;
    p {
      margin: 0 0 6px;
      font-weight: 700;
      font-size: 14px;
    }
    iframe,
    video {
      width: 100%;
      max-width: 720px;
      aspect-ratio: 16 / 9;
      border: 0;
      border-radius: 8px;
      background: #000;
    }
  }

  .ce-rv-q {
    border-bottom: 1px solid #eef0f2;
    padding: 12px 4px;
  }
  .ce-rv-q:last-child {
    border-bottom: 0;
  }
  .ce-rv-q-head {
    display: flex;
    gap: 10px;
    align-items: baseline;
    flex-wrap: wrap;
    font-size: 14px;

    .seq {
      font-weight: 700;
      color: #5b6774;
      min-width: 44px;
    }
    .mark-ok {
      color: #2f5d50;
      font-weight: 700;
    }
    .mark-no {
      color: #a8432a;
      font-weight: 700;
    }
    .mark-empty {
      color: #9aa4ae;
    }
    .yours {
      color: #1d2430;
    }
    .expected {
      color: #c25a32;
    }
  }
  /* 審題／答題順序／詳解 三個介面的分頁列，與 Passage／Part 切換 */
  .ce-rv-tabs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 4px 0 16px;
  }
  .ce-rv-tab {
    border: 1px solid #d8cbb8;
    background: #fff;
    border-radius: 18px;
    padding: 5px 16px;
    font-size: 15px;
    cursor: pointer;
    color: #3e2f1f;
  }
  .ce-rv-tab.is-active {
    background: #3e2f1f;
    border-color: #3e2f1f;
    color: #fff;
  }
  .ce-rv-tab--page {
    font-size: 13px;
    padding: 3px 12px;
  }
  .ce-rv-tabs-sep {
    flex: 1;
  }
  .ce-rv-group {
    border: 1px solid #eadfcf;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 14px;
    background: #fffdf9;
  }
  .ce-rv-group > h3 {
    margin: 0 0 6px;
    font-size: 16px;
  }
  .ce-rv-group-explain {
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #eadfcf;
  }

  /* 鎖定的詳解（未開通的學生）：內容在 API 端就已遮掉，這裡只顯示說明 */
  .ce-rv-locked {
    margin-top: 8px;
    padding: 8px 12px;
    border: 1px dashed #b0a49a;
    border-radius: 6px;
    background: #faf6ef;
    color: #6d5c4d;
    font-size: 14px;
  }

  .ce-rv-q-explain {
    margin-top: 8px;
    padding: 10px 14px;
    background: #fbf9f5;
    border-left: 3px solid #efc180;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.75;
  }

  /* 老師標的重點句（文章裡）；點題目時捲過去並閃一下 */
  .ce-rv-hl {
    background: #fdf0b7;
    border-bottom: 2px solid #e8c243;
  }
  .ce-rv-hl--flash {
    animation: ceRvFlash 1.6s ease-out;
  }
  @keyframes ceRvFlash {
    0% { background: #f5b940; }
    100% { background: #fdf0b7; }
  }
  .ce-rv-q--jump {
    cursor: pointer;
  }
  .ce-rv-q--jump:hover {
    background: #f7f9fb;
  }
  .ce-rv-q-pin {
    font-size: 12px;
    color: #b07d1a;
    white-space: nowrap;
  }

  .ce-rv-essay {
    white-space: pre-wrap;
    font-size: 15px;
    line-height: 1.8;
    background: #fbfbfc;
    border: 1px solid #eef0f2;
    border-radius: 6px;
    padding: 14px 16px;
  }
  .ce-rv-model {
    border: 1px solid #e4e7ea;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 12px;

    .kind {
      display: inline-block;
      font-size: 12px;
      padding: 2px 10px;
      border-radius: 10px;
      background: #eef3f8;
      color: #33566b;
      margin-bottom: 6px;
    }
    .title {
      font-weight: 700;
      margin: 0 0 8px;
      font-size: 15px;
    }
    .body {
      font-size: 15px;
      line-height: 1.8;
    }
    img {
      max-width: 100%;
    }
    embed {
      width: 100%;
      height: 640px;
    }
  }

  .ce-rv-note {
    color: #9aa4ae;
    font-size: 14px;
  }

  .ce-rv-center {
    max-width: 520px;
    margin: 16vh auto 0;
    background: #fff;
    border-radius: 10px;
    padding: 28px;
    text-align: center;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  }
`;

const isYouTube = (url) => /youtube\.com|youtu\.be/.test(url || "");
const isImageUrl = (url) => /\.(png|jpe?g)$/i.test(String(url || "").split("?")[0]);
const isPdfUrl = (url) => /\.pdf$/i.test(String(url || "").split("?")[0]);

const KIND_WORDING = { teacher: "老師範文", cambridge: "劍橋官方範文" };

const ReviewPage = ({ section, password, onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const passageRef = useRef(null);
  // Edric 2026-10-20：詳解分三個介面——審題、答題順序、詳解（以題組為單位），
  // 都以 Passage／Part 為單位切換
  const [tab, setTab] = useState("detail");
  const [pageIdx, setPageIdx] = useState(0);

  useEffect(() => {
    axios
      .post(`${process.env.REACT_APP_EXAM_DETAILS}/${section.id}/review`, { password })
      .then((res) => {
        if (res.data.success) {
          setData(res.data);
        } else {
          setError(res.data.message || "無法載入詳解，請稍後再試。");
        }
      })
      .catch(() => setError("連線失敗，請稍後再試。"));
  }, [section.id, password]);

  const review = (data && data.review) || {};
  const vocabularies = review.vocabularies || [];

  // 重點句套色：拿每題的錨點文字，在右邊文章的對應頁裡找到該句包起來。
  // 找不到（教務後來改了文章）就靜默跳過，頁面不會壞。
  useEffect(() => {
    const root = passageRef.current;
    if (!data || data.type !== "R" || !root) {
      return;
    }
    clearHighlightsIn(root, "ce-rv-hl");
    const pageEls = root.querySelectorAll(".ce-rv-passage");
    (data.results || []).forEach((r) => {
      (r.highlights || []).forEach((h) => {
        highlightTextIn(pageEls[h.page] || root, h.text, {
          className: "ce-rv-hl",
          attrs: { "data-seq": String(r.seq) },
        });
      });
    });
    // tab：切到審題／答題順序再切回詳解時文章欄是重新掛上的，要重套
  }, [data, tab]);

  /** 點題目 → 文章捲到該題的重點句並閃一下 */
  const jumpToHighlight = (seq) => {
    const root = passageRef.current;
    if (!root) {
      return;
    }
    const marks = root.querySelectorAll(`.ce-rv-hl[data-seq="${seq}"]`);
    if (marks.length === 0) {
      return;
    }
    marks[0].scrollIntoView({ block: "center", behavior: "smooth" });
    marks.forEach((el) => {
      el.classList.remove("ce-rv-hl--flash");
      void el.offsetWidth; // 讓瀏覽器吃掉 class 移除，動畫才會重跑
      el.classList.add("ce-rv-hl--flash");
    });
  };

  const renderVideos = () =>
    (review.videos || []).length > 0 && (
      <div className="ce-rv-block">
        <h2>詳解影片</h2>
        {review.videos.map((v, i) => (
          <div key={v.id || i} className="ce-rv-video">
            {v.title && <p>{v.title}</p>}
            {v.locked && !v.url ? (
              <div className="ce-rv-locked">{LOCKED_TEXT}</div>
            ) : isYouTube(v.url) ? (
              <iframe
                src={v.url}
                title={v.title || `詳解影片 ${i + 1}`}
                allow="accelerometer; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video src={v.url} controls controlsList="nodownload" />
            )}
          </div>
        ))}
      </div>
    );

  const renderQuestion = (r) => {
    const hasHighlight = data.type === "R" && (r.highlights || []).length > 0;
    return (
    <div
      key={r.seq}
      className={`ce-rv-q ${hasHighlight ? "ce-rv-q--jump" : ""}`}
      onClick={hasHighlight ? () => jumpToHighlight(r.seq) : undefined}
      title={hasHighlight ? "點一下，文章會捲到本題的重點句" : undefined}
    >
      <div className="ce-rv-q-head">
        <span className="seq">第 {r.seq} 題</span>
        {hasHighlight && <span className="ce-rv-q-pin">📍文章重點</span>}
        {r.ok ? (
          <span className="mark-ok">✔ 答對</span>
        ) : r.answered ? (
          <span className="mark-no">✘ 答錯</span>
        ) : (
          <span className="mark-empty">— 未作答</span>
        )}
        <span className="yours">你的作答：{r.answered ? r.student : "（未作答）"}</span>
        <span className="expected">正解：{(r.expected || []).join(" / ")}</span>
      </div>
      {r.explanation_html && (
        <div className="ce-rv-q-explain">
          <HtmlWithVocab html={r.explanation_html} vocabularies={vocabularies} />
        </div>
      )}
      {!r.explanation_html && r.explanation_locked && <div className="ce-rv-locked">{LOCKED_TEXT}</div>}
    </div>
    );
  };

  const sections = review.sections || [];
  const groups = review.groups || [];
  const sectionOf = (page, kind, key = "") =>
    sections.find((x) => x.page === page && x.kind === kind && String(x.group_key || "") === key);
  const pageLabel = (i) => (data.type === "L" ? `Part ${i + 1}` : `Passage ${i + 1}`);

  /** 一則審題／答題順序／題組詳解：有內容顯示內容、鎖住顯示試閱提示、沒寫就顯示 empty */
  const renderSection = (row, empty) => {
    if (row && row.content_html) {
      return <HtmlWithVocab html={row.content_html} vocabularies={vocabularies} />;
    }
    if (row && row.locked) {
      return <div className="ce-rv-locked">{LOCKED_TEXT}</div>;
    }
    return empty ? <p className="ce-rv-note">{empty}</p> : null;
  };

  const renderTabs = () => (
    <div className="ce-rv-tabs">
      {[
        ["overview", "審題"],
        ["order", "答題順序"],
        ["detail", "詳解"],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          className={`ce-rv-tab ${tab === key ? "is-active" : ""}`}
          onClick={() => setTab(key)}
        >
          {label}
        </button>
      ))}
      <span className="ce-rv-tabs-sep" />
      {(data.pages || []).map((p, i) => (
        <button
          key={i}
          type="button"
          className={`ce-rv-tab ce-rv-tab--page ${pageIdx === i ? "is-active" : ""}`}
          onClick={() => setPageIdx(i)}
        >
          {pageLabel(i)}
        </button>
      ))}
    </div>
  );

  const renderOverviewOrOrder = (kind) => (
    <div className="ce-rv-block">
      <h2>
        {pageLabel(pageIdx)}｜{kind === "overview" ? "審題" : "答題順序"}
      </h2>
      {renderSection(
        sectionOf(pageIdx, kind),
        kind === "overview" ? "老師還沒提供這一篇的審題。" : "老師還沒提供這一篇的答題順序。"
      )}
    </div>
  );

  /** 詳解：這一篇的每個題組＝題組詳解＋底下逐題 */
  const renderGroupedQuestions = () => {
    const resultBySeq = {};
    (data.results || []).forEach((r) => {
      resultBySeq[Number(r.seq)] = r;
    });
    const covered = {};
    groups.forEach((g) => g.seqs.forEach((n) => (covered[n] = true)));
    const pageGroups = groups.filter((g) => g.page === pageIdx);
    // 沒落在任何題組的題目（資料異常時）放最後一篇，不讓它消失
    const orphans =
      pageIdx === (data.pages || []).length - 1
        ? (data.results || []).filter((r) => !covered[Number(r.seq)])
        : [];
    return (
      <div className="ce-rv-block">
        <h2>{pageLabel(pageIdx)}｜詳解</h2>
        {pageGroups.map((g) => (
          <div key={g.key} className="ce-rv-group">
            <h3>{g.label}</h3>
            {sectionOf(pageIdx, "group", g.key) && (
              <div className="ce-rv-group-explain">{renderSection(sectionOf(pageIdx, "group", g.key))}</div>
            )}
            {g.seqs.map((n) => resultBySeq[n]).filter(Boolean).map(renderQuestion)}
          </div>
        ))}
        {orphans.length > 0 && (
          <div className="ce-rv-group">
            <h3>其他題目</h3>
            {orphans.map(renderQuestion)}
          </div>
        )}
        {pageGroups.length === 0 && orphans.length === 0 && <p className="ce-rv-note">這一篇沒有題目。</p>}
      </div>
    );
  };

  const renderQuestions = () => (
    <div className="ce-rv-block">
      <h2>逐題檢討</h2>
      {(data.results || []).map(renderQuestion)}
      {!(data.results || []).some((r) => r.explanation_html || r.explanation_locked) && (
        <p className="ce-rv-note">這份考卷的老師詳解還在準備中，先提供正確答案對照。</p>
      )}
    </div>
  );

  const renderPassages = () => (
    <div className="ce-rv-block" ref={passageRef}>
      <h2>文章</h2>
      {/* 各篇都渲染（重點句以頁索引找文章），只顯示目前選的那篇 */}
      {(data.pages || []).map((page, i) => (
        <div key={i} className="ce-rv-passage" style={groups.length > 0 && i !== pageIdx ? { display: "none" } : undefined}>
          {page.title && <h3>{page.title}</h3>}
          <HtmlWithVocab html={page.content || ""} vocabularies={vocabularies} />
        </div>
      ))}
    </div>
  );

  const renderTranscript = () =>
    (review.transcript || []).length > 0 && (
      <div className="ce-rv-block">
        <h2>逐字稿（點句子可重播該句）</h2>
        <TranscriptPlayer
          audio={data.audio}
          sentences={review.transcript}
          vocabularies={vocabularies}
        />
      </div>
    );

  const renderWriting = () => {
    const essays = review.essays || [];
    return [1, 2].map((task) => {
      const prompt = (data.pages || [])[task - 1];
      const mine = (data.studentEssays || [])[task - 1];
      const models = essays.filter((e) => Number(e.task_no) === task);
      if (!prompt && !mine && models.length === 0) {
        return null;
      }
      return (
        <div key={task} className="ce-rv-block">
          <h2>Task {task}</h2>
          {prompt && prompt.content && (
            <details>
              <summary style={{ cursor: "pointer", marginBottom: "8px" }}>題目</summary>
              <div className="ce-rv-passage">
                <HtmlWithVocab html={prompt.content} vocabularies={vocabularies} />
              </div>
            </details>
          )}
          <div className="ce-rv-columns">
            <div className="ce-rv-col">
              <h3>你的作文</h3>
              {mine && mine.trim() !== "" ? (
                <div className="ce-rv-essay">{mine}</div>
              ) : (
                <p className="ce-rv-note">（未作答）</p>
              )}
            </div>
            <div className="ce-rv-col">
              <h3>範文</h3>
              {models.length === 0 && <p className="ce-rv-note">這一題的範文還在準備中。</p>}
              {models.map((m, i) => (
                <div key={m.id || i} className="ce-rv-model">
                  <span className="kind">{KIND_WORDING[m.kind] || m.kind}</span>
                  {m.title && <p className="title">{m.title}</p>}
                  {m.locked && !m.content_html && !m.file_url && (
                    <div className="ce-rv-locked">{LOCKED_TEXT}</div>
                  )}
                  {m.content_html && (
                    <div className="body">
                      <HtmlWithVocab html={m.content_html} vocabularies={vocabularies} />
                    </div>
                  )}
                  {m.file_url && isImageUrl(m.file_url) && (
                    <img src={m.file_url} alt={m.title || "範文"} />
                  )}
                  {m.file_url && isPdfUrl(m.file_url) && (
                    <embed src={m.file_url} type="application/pdf" />
                  )}
                  {m.file_url && !isImageUrl(m.file_url) && !isPdfUrl(m.file_url) && (
                    <a href={m.file_url} target="_blank" rel="noopener noreferrer">
                      開啟範文檔案
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    });
  };

  // ---- 載入中／錯誤／尚未開放 ----
  if (error || !data || data.ready === false) {
    return (
      <StyledReview>
        <div className="ce-rv-center">
          {!data && !error && <p>詳解載入中…</p>}
          {error && <p style={{ color: "#a8432a" }}>{error}</p>}
          {data && data.ready === false && <p>{data.message || "完成所有科目後才能查看詳解。"}</p>}
          <button type="button" className="ce-rv-back" onClick={onBack}>
            回到測驗首頁
          </button>
        </div>
      </StyledReview>
    );
  }

  return (
    <StyledReview>
      <div className="ce-rv-inner">
        <div className="ce-rv-top">
          <h1>{section.name}｜詳解</h1>
          {data.score && (
            <span className="ce-rv-score">
              {`Band ${Number(data.score.band).toFixed(1)}　答對 ${data.score.correct} / ${data.score.total} 題`}
            </span>
          )}
          <button type="button" className="ce-rv-back" onClick={onBack}>
            ← 回到測驗首頁
          </button>
        </div>

        {renderVideos()}

        {/* 有題組資料（API 2026-10-20 之後）才走三介面；舊資料退回原本的逐題清單 */}
        {data.type !== "W" && groups.length > 0 && renderTabs()}

        {data.type !== "W" && groups.length > 0 && tab !== "detail" && renderOverviewOrOrder(tab)}

        {/* Edric 指定：題目在左、文章在右；文章欄釘住自己捲 */}
        {data.type === "R" && (groups.length === 0 || tab === "detail") && (
          <div className="ce-rv-columns">
            <div className="ce-rv-col">{groups.length > 0 ? renderGroupedQuestions() : renderQuestions()}</div>
            <div className="ce-rv-col ce-rv-col--pin">{renderPassages()}</div>
          </div>
        )}

        {data.type === "L" && (groups.length === 0 || tab === "detail") && (
          <React.Fragment>
            {groups.length > 0 ? renderGroupedQuestions() : renderQuestions()}
            {renderTranscript()}
          </React.Fragment>
        )}

        {data.type === "W" && renderWriting()}
      </div>
    </StyledReview>
  );
};

export default ReviewPage;
